import { useState } from 'react';
import type { Panel, IntercomConfig } from '@livepuls/types';
import { GripVertical } from 'lucide-react';
import { StatusDot } from '../ui/StatusDot.js';
import { PanelSlot } from './PanelSlot.js';

interface Props {
  panel: Panel;
  draft: IntercomConfig;
  online?: boolean;
  selected?: boolean;
  selectedButtonId?: string | null | undefined;
  onSelect?: () => void;
  onAddSlot?: ((position: number) => void) | undefined;
  onButtonClick?: ((buttonId: string) => void) | undefined;
  /** Button-IDs, die gerade angesprochen werden → orange Hervorhebung. */
  receivingButtonIds?: ReadonlySet<string> | undefined;
  /** Buttons dieses Panels per HTML5-Drag verschiebbar machen. */
  draggableButtons?: boolean;
  /** Drop eines Buttons auf einen konkreten Slot → dorthin verschieben. */
  onMoveToSlot?: ((buttonId: string, position: number) => void | Promise<void>) | undefined;
  /**
   * Wenn gesetzt: Panel akzeptiert Drops eines anderen Buttons und
   * dupliziert ihn via Callback auf dieses Panel.
   */
  onDropButton?: (buttonId: string) => void | Promise<void>;
  /** Drag eines eigenen Buttons ohne Ziel beendet → Button löschen. */
  onButtonDragOut?: (buttonId: string) => void | Promise<void>;
  /** Rechtsklick auf belegten Button → Kontextmenü. */
  onButtonContextMenu?: (
    buttonId: string,
    pos: { x: number; y: number },
  ) => void;
  /** Rechtsklick auf leeren Slot → Kontextmenü (Neu / Einfügen). */
  onEmptyContextMenu?: (
    position: number,
    pos: { x: number; y: number },
  ) => void;
  /** Remote-Modus: ferngesetzte Pegel pro Button-ID. */
  buttonVolumes?: Record<string, number> | undefined;
  /** Remote-Modus: Pegel eines Buttons ändern (Drag im Chip). */
  onButtonVolumeChange?: ((buttonId: string, volume: number) => void) | undefined;
}

export function PanelCard({
  panel,
  draft,
  online = false,
  selected,
  selectedButtonId,
  onSelect,
  onAddSlot,
  onButtonClick,
  receivingButtonIds,
  draggableButtons,
  onMoveToSlot,
  onDropButton,
  onButtonDragOut,
  onButtonContextMenu,
  onEmptyContextMenu,
  buttonVolumes,
  onButtonVolumeChange,
}: Props): JSX.Element {
  const occupied = panel.layout.filter((s) => s.buttonId).length;
  const [dragOver, setDragOver] = useState(false);

  return (
    <section
      onClick={onSelect}
      onDragOver={
        onDropButton
          ? (e) => {
              if (e.dataTransfer.types.includes('application/x-livepuls-button-id')) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
                if (!dragOver) setDragOver(true);
              }
            }
          : undefined
      }
      onDragLeave={onDropButton ? () => setDragOver(false) : undefined}
      onDrop={
        onDropButton
          ? (e) => {
              e.preventDefault();
              setDragOver(false);
              const id = e.dataTransfer.getData('application/x-livepuls-button-id');
              if (id) void onDropButton(id);
            }
          : undefined
      }
      className={[
        'panel-shell flex flex-col cursor-pointer transition',
        selected ? 'ring-1 ring-accent' : '',
        dragOver ? 'ring-2 ring-accent bg-[#1f1b12]' : '',
      ].join(' ')}
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2.5 min-w-0">
          <GripVertical size={14} className="text-text-mute shrink-0" />
          <StatusDot status={online ? 'ok' : 'idle'} />
          <h3 className="text-sm font-semibold text-text truncate">{panel.name}</h3>
          <span className="text-xxs text-text-mute shrink-0">
            {occupied}/{panel.layout.length}
          </span>
        </div>
      </header>

      <div className="p-3 grid grid-cols-3 gap-2">
        {panel.layout.map((slot) => (
          <PanelSlot
            key={slot.position}
            slot={slot}
            draft={draft}
            onAdd={onAddSlot}
            onButtonClick={onButtonClick}
            selectedButtonId={selectedButtonId ?? null}
            receiving={slot.buttonId ? receivingButtonIds?.has(slot.buttonId) ?? false : false}
            draggable={draggableButtons ?? false}
            onMoveToSlot={onMoveToSlot}
            onButtonDragOut={onButtonDragOut}
            onButtonContextMenu={onButtonContextMenu}
            onEmptyContextMenu={onEmptyContextMenu}
            volume={slot.buttonId ? buttonVolumes?.[slot.buttonId] : undefined}
            onVolumeChange={onButtonVolumeChange}
          />
        ))}
      </div>
    </section>
  );
}
